// pathway-builder.js — derive squiggle pathway pages from item lists.
//
// PORT TARGET:
//   Assets/KFE/Scripts/Runtime/Core/UI/Pathway/PathwayScreen.cs
//     ApplyPathwayLocking + BuildPages
//   Assets/KFE/Scripts/Runtime/Core/ContentModels.cs
//     ItemState / NodeState
//
// Brief 002b: the input is still a flat ordered list of pathway-eligible
// items. The adapter marks the highest-order-index item as the star
// milestone (stage 77 in stage view, the last lesson of a stage in lesson
// view). Brief 022 will replace the flat input with a true pathway loader
// keyed off kfe_pathways.json that pulls stage / lesson hierarchies from
// the content DB.
//
// Brief 011: locking decisions are delegated to `./gating.js` so the
// squiggle and the lesson-detail screen share a single source of truth.

import { getItemProgress, getSetting, getAllRollupCache } from "./learner-store.js";
import { buildSingleFolderTree, getInaccessibleItems, isLessonComplete } from "./gating.js";
import { isGatingExemptId } from "./gating-config.js";
import { loadPathway, loadLesson } from "./pathway-loader.js";
import { bareItemId, isGateType, isMeterType } from "./lesson-nav.js";
import { resolveRequiredPlay, isCompletableItem } from "./item-completion.js";

/** @typedef {"locked" | "available" | "completed"} NodeState */

/**
 * @typedef {object} ItemState
 * @property {string|number} itemId
 * @property {string}    title
 * @property {string}    [titleZh]
 * @property {NodeState} nodeState
 * @property {number}    playCount
 * @property {number}    requiredPlay
 * @property {boolean}   isGate            excluded from squiggle nodes
 * @property {boolean}   isMeter           excluded from squiggle nodes
 * @property {boolean}   isIntroItem       excluded from squiggle nodes
 * @property {boolean}   [isGatingExempt]  Brief 150 — self-assessment, invisible to gating
 * @property {boolean}   isStarMilestone   render as a star instead of a number
 * @property {number}    meterType
 * @property {number}    meterProgress     0..1
 * @property {import("./gating.js").GateRule} [gateRule]
 * @property {number[]}  [requiredMmuIds]
 * @property {number}    [earliestUnlockAtMs]
 */

/**
 * @typedef {object} PathwayPage
 * @property {ItemState[]} nodes
 * @property {number}      startNumber  display number of the first node (1-based)
 * @property {boolean}     hasNextPage
 */

/**
 * Page size matches iOS LessonsViewController.m's `stagesPerPage` and Unity's
 * default. Don't change without coordinating both ports.
 */
export const PAGE_SIZE = 16;

/**
 * @typedef {object} PathwayInputItem
 * @property {string|number} itemId
 * @property {string} title
 * @property {string} [titleZh]
 * @property {number} [requiredPlay]
 */

/**
 * @typedef {object} OrderedPathwayNode
 * One entry from a pathway tree's stages[] or stage.lessons[] array. May be
 * a renderable lesson/stage node, or a gate item that participates in
 * locking but is filtered out of the rendered squiggle. The order of the
 * input array IS the gating order — gates before subsequent items lock
 * those items per the iOS gate rules.
 *
 * @property {string|number} itemId
 * @property {string} [title]
 * @property {string} [titleZh]
 * @property {boolean} [isGate]
 * @property {boolean} [isIntroItem]
 * @property {boolean} [isGatingExempt]   Brief 150 — self-assessment, invisible to gating
 * @property {boolean} [excludeFromGating] Brief 150 — content flag (preferred source of isGatingExempt)
 * @property {number} [requiredPlay]
 * @property {boolean} [derivedComplete]  Brief 151 — when set, the node's
 *   completion is supplied by the caller (a live stage-rollup derive) instead of
 *   read from the learner store. Stage folder ids never carry a `completedCount`
 *   row (nothing "plays" a stage), so `pathwayFromOrderedNodes` honours this flag
 *   as the node's completed/not-completed signal for both its squiggle state and
 *   any downstream gate's upstream-window check.
 * @property {import("./gating.js").GateRule} [gateRule]
 * @property {number[]} [requiredMmuIds]
 * @property {number} [earliestUnlockAtMs]
 */

/**
 * Build pathway pages from a flat list of pathway-eligible items.
 *
 * Reads each item's progress from the learner-store to set its initial
 * `nodeState` (Completed if completedCount > 0, else Locked), then runs the
 * Unity port's `ApplyPathwayLocking`: walk in order, the first non-Completed
 * item becomes Available; everything after it stays Locked. Marks the very
 * last item as the star milestone. Splits the list into pages of `PAGE_SIZE`.
 *
 * Gating is handled visually in <kfe-squiggle> based on its `gatingEnabled`
 * prop — this builder always emits the canonical locked/available state
 * model.
 *
 * @param {PathwayInputItem[]} items
 * @returns {Promise<PathwayPage[]>}
 */
export async function pathwayFromItems(items) {
  /** @type {OrderedPathwayNode[]} */
  const nodes = items.map((it) => ({
    itemId: it.itemId,
    title: it.title,
    titleZh: it.titleZh,
    requiredPlay: it.requiredPlay,
  }));
  return pathwayFromOrderedNodes(nodes);
}

/**
 * Build pathway pages from an ordered node list that may include gate
 * items. Gates participate in the gating tree (so subsequent items lock
 * correctly per `iOS getInaccessibleItems`) but never appear as squiggle
 * nodes — they're stripped before pagination.
 *
 * Brief 022: this is the entry point used by `<kfe-pathway>` once the
 * pathway data layer lands. The loader (`pathway-loader.js`) hands over
 * the raw stages[] / lessons[] list with gates inline; this function does
 * the rest.
 *
 * @param {OrderedPathwayNode[]} nodes
 * @returns {Promise<PathwayPage[]>}
 */
export async function pathwayFromOrderedNodes(nodes) {
  /** @type {ItemState[]} */
  const states = [];
  /** @type {Record<string, number>} */
  const completedCounts = {};
  for (const n of nodes) {
    const isGate = n.isGate === true;
    const isIntroItem = n.isIntroItem === true;
    // Brief 150: exemption is data-driven — the content `excludeFromGating`
    // flag (preferred) OR the static fallback set, so it holds before and
    // after a corpus regen.
    const isGatingExempt = isGatingExemptId(n.itemId, n.excludeFromGating) || n.isGatingExempt === true;
    // Brief 151: a stage folder id never carries a `completedCount` row — nothing
    // "plays" a stage. When the caller has derived the stage's completion live
    // (the Option-A roll-up), honour that flag instead of the always-zero store
    // read. We map it onto a synthetic completedCount (>= requiredPlay when
    // complete, else 0) so the existing nodeState + gate-window machinery below
    // needs no special-casing.
    let completedCount;
    if (typeof n.derivedComplete === "boolean") {
      completedCount = n.derivedComplete ? (n.requiredPlay ?? 1) : 0;
    } else {
      // Gates have no learner progress today (no flow advances them yet).
      // Reading the store anyway keeps the door open for brief 011's gate-
      // satisfaction flow without changing this signature.
      const progress = /** @type {{completedCount?: number} | undefined} */ (
        await getItemProgress(/** @type {any} */ (n.itemId))
      );
      completedCount = progress?.completedCount ?? 0;
    }
    completedCounts[String(n.itemId)] = completedCount;
    states.push({
      itemId: n.itemId,
      title: n.title ?? "",
      titleZh: n.titleZh,
      // iOS canon: GatingAPI.m:68-81 `canAdvanceNext:itemId:` uses
      // `completed >= required_play`. Source `requiredPlay` from the same
      // place gating.js:211 does (the catalog default, not learner-store's
      // meter-bumped value) so the squiggle's "completed" label and the
      // gating module's lock decision can never diverge. Brief 071 §4.1
      // catalogued the prior `> 0` asymmetry; Brief 082 closed it.
      nodeState: completedCount >= (n.requiredPlay ?? 1) ? "completed" : "locked",
      playCount: completedCount,
      requiredPlay: n.requiredPlay ?? 1,
      isGate,
      isMeter: false,
      isIntroItem,
      isGatingExempt,
      isStarMilestone: false,
      meterType: 0,
      meterProgress: 0,
      gateRule: n.gateRule,
      requiredMmuIds: n.requiredMmuIds,
      earliestUnlockAtMs: n.earliestUnlockAtMs,
    });
  }
  const gatingEnabled = (await getSetting("gating_enabled")) !== false;
  applyPathwayLocking(states, { completedCounts, gatingEnabled });

  // Gates are excluded from the rendered squiggle (Brief 022 success
  // criterion #4). Intro items are excluded too — Brief 032 surfaces them
  // as a banner above the squiggle instead of as numbered nodes (mirrors
  // iOS LessonsViewController guideView + Unity SetupGuidePanel).
  const visible = states.filter((s) => !s.isGate && !s.isMeter && !s.isIntroItem);
  if (visible.length > 0) {
    visible[visible.length - 1].isStarMilestone = true;
  }
  return paginate(visible);
}

/**
 * Mirrors PathwayScreen.cs `ApplyPathwayLocking` but delegates the
 * inaccessibility decision to `gating.js`. Walks in order; the first
 * non-Completed visible node becomes Available; everything after it
 * stays Locked. Completed nodes are untouched. Gates and meters are skipped
 * entirely (they affect locking via the underlying state model but never
 * render as squiggle nodes). Brief 133: intros are NO LONGER skipped — they
 * participate in the inaccessibility check (gating.js keeps them always
 * unlocked while letting an incomplete intro gate downstream items).
 *
 * @param {ItemState[]} states
 * @param {{completedCounts?: Record<string, number>, gatingEnabled?: boolean}} [learnerState]
 */
export function applyPathwayLocking(states, learnerState) {
  /** @type {import("./gating.js").ItemNode[]} */
  const nodes = states.map((s) => ({
    id: s.itemId,
    isGate: s.isGate,
    isMeter: s.isMeter,
    isIntroItem: s.isIntroItem,
    isGatingExempt: s.isGatingExempt,
    requiredPlay: s.requiredPlay,
    gateRule: s.gateRule,
    requiredMmuIds: s.requiredMmuIds,
    earliestUnlockAtMs: s.earliestUnlockAtMs,
  }));
  const tree = buildSingleFolderTree("__pathway__", nodes);

  // Synthesise completedCounts from the input states when the caller
  // didn't provide them — keeps applyPathwayLocking callable in isolation
  // (e.g. tests) using only the ItemState array's own progress fields.
  /** @type {Record<string, number>} */
  const completedCounts = (learnerState?.completedCounts) ? { ...learnerState.completedCounts } : {};
  for (const s of states) {
    if (completedCounts[String(s.itemId)] != null) continue;
    completedCounts[String(s.itemId)] = s.nodeState === "completed" ? Math.max(s.playCount ?? 1, s.requiredPlay) : (s.playCount ?? 0);
  }

  const inaccessible = new Set(
    getInaccessibleItems("__pathway__", {
      gatingEnabled: learnerState?.gatingEnabled ?? true,
      completedCounts,
    }, tree).map(String),
  );

  // Brief 133: intros are NO LONGER skipped here. They flow into the
  // `inaccessible.has()` check like any other item — gating.js keeps them out
  // of the inaccessible set (always-unlocked per iOS canon) while letting an
  // incomplete intro gate the items after it. Gates and meters stay skipped:
  // they never render as squiggle nodes and carry no own progress.
  for (const s of states) {
    if (s.isGate || s.isMeter) continue;
    if (s.nodeState === "completed") continue;
    s.nodeState = inaccessible.has(String(s.itemId)) ? "locked" : "available";
  }
}

/**
 * Derive a single stage's completion LIVE from its lessons' per-item progress —
 * the web Option-A roll-up (Brief 151; see gating.js `isStageComplete` for the
 * iOS `updateFolderCompletedState` predicate it ports). Loads each lesson's items
 * + the learner's `completedCount` / meter-bumped `requiredPlay` from the store,
 * classifies them with the same gate/meter/intro rules the lesson chart uses, and
 * applies the pure `isStageComplete` predicate.
 *
 * Short-circuits per lesson: returns at the FIRST incomplete required lesson, so
 * a barely-started stage costs ~one lesson fetch, not the whole stage (the cost
 * note in Brief 151 §Design). Gate "lessons" (`type: "gate"`) and Brief 150
 * exempt lessons are skipped — they are not required children.
 *
 * Conservative on failure: a lesson whose JSON can't be loaded counts as NOT
 * complete (the stage stays incomplete → its downstream gate stays locked),
 * mirroring `isLessonLocked`'s never-over-unlock disposition.
 *
 * @param {string} pathwayId
 * @param {import("./pathway-loader.js").PathwayStage} stage
 * @returns {Promise<boolean>}
 */
export async function deriveStageComplete(pathwayId, stage) {
  // iOS canon (Rule #9): a folder with no required children is never marked
  // complete (`LessonsViewController.m:131` `itemsNumber != 0`; gate query `:58-59`
  // `items_number > 0`). Count the required (non-gate, non-exempt) lessons and
  // return complete only if at least one existed AND all were complete — a stage
  // whose children are all gates/exempt must NOT cascade to spurious green
  // (Brief 154). No real ZTF stage hits this today; it is a conservative guard.
  let requiredLessons = 0;
  for (const lessonRef of stage?.lessons ?? []) {
    if (!lessonRef) continue;
    if (lessonRef.type === "gate") continue;
    if (isGatingExemptId(lessonRef.id, /** @type {any} */ (lessonRef).excludeFromGating)) continue;
    requiredLessons++;
    if (!(await deriveLessonComplete(pathwayId, lessonRef))) return false;
  }
  return requiredLessons > 0;
}

/**
 * Derive a lesson's completion LIVE from its items' per-item progress — the
 * lesson level of the recursive lesson→stage roll-up (Brief 151's
 * `deriveStageComplete` calls this per child lesson; Brief 153 exports it so the
 * lesson-level squiggle can render the iOS-canonical "completed" green directly).
 *
 * Mirrors iOS `LessonsViewController.m:126 updateFolderCompletedState` at the
 * folder (lesson) level: a folder is complete iff every required child item is
 * complete (`items_completed_number >= items_number - items_not_required_number`).
 * "Not required" = a child with `required_play` 0; routed through `isCompletableItem`
 * so an authored rp0 content item auto-satisfies (Brief 149b regression guard).
 *
 * Builds the lesson's gating nodes (with the effective `resolveRequiredPlay`
 * threshold, Brief 125) + the learner's `completedCounts`, then defers to the
 * pure `isLessonComplete` — the SAME predicate `maybeFireLessonCompleted` uses,
 * so the rollup and the lesson-completed event can never diverge.
 *
 * Async (lesson items are lazy-loaded). Uses memoised `loadLesson` so multiple
 * derives in one render pass are cheap. Conservative on failure: a lesson whose
 * JSON can't load counts as NOT complete (never over-unlock).
 *
 * @param {string} pathwayId
 * @param {import("./pathway-loader.js").PathwayLessonRef} lessonRef
 * @returns {Promise<boolean>}
 */
export async function deriveLessonComplete(pathwayId, lessonRef) {
  /** @type {{items?: any[]}} */
  let lesson;
  try {
    lesson = /** @type {any} */ (await loadLesson(pathwayId, lessonRef.ref || String(lessonRef.id)));
  } catch {
    return false; // can't verify → treat as incomplete (never over-unlock)
  }
  const rawItems = Array.isArray(lesson?.items) ? lesson.items : [];
  // iOS canon (Rule #9): a folder with NO children is never marked complete.
  // `LessonsViewController.m:131` only evaluates folder completion under
  // `if (completed == 0 && itemsNumber != 0)`, and the gate's upstream query
  // (`:58-59`) requires `items_number > 0`. A childless / failed-to-populate
  // lesson (items: []) is therefore NOT complete — return false (conservative,
  // never over-unlock). Brief 154: the prior `return true` (vacuous-complete)
  // diverged from iOS and could cascade a stage to spurious green. Note: a
  // lesson that DOES have items but whose only items are gates/meters/intros/
  // rp0-content is a DIFFERENT case (iOS `items_number > 0`, all not-required →
  // `0 >= 0` complete); that is handled below by `isLessonComplete`'s vacuous
  // visible-set, and must stay complete so an intro-only lesson never blocks the
  // Brief 151 stage roll-up.
  if (rawItems.length === 0) return false;

  /** @type {import("./gating.js").ItemNode[]} */
  const nodes = [];
  /** @type {Record<string, number>} */
  const completedCounts = {};
  await Promise.all(rawItems.map(async (it) => {
    const id = bareItemId(it?.id);
    const isGate = isGateType(it?.type);
    const isMeter = isMeterType(it?.type);
    const isIntroItem = it?.isIntro === true;
    const isGatingExempt = isGatingExemptId(id, /** @type {any} */ (it)?.excludeFromGating);
    // Only the visible, non-exempt items drive completion — skip the store read
    // for gates/meters/intros/exempt nodes that `isLessonComplete` ignores.
    if (isGate || isMeter || isIntroItem || isGatingExempt) {
      nodes.push({ id, isGate, isMeter, isIntroItem, isGatingExempt });
      return;
    }
    // Mirror kfe-lesson-detail._computeInaccessible EXACTLY so the stage
    // roll-up and the within-lesson gate can never disagree: only COMPLETABLE
    // items carry a resolveRequiredPlay (meter-bumped) threshold + a store read.
    // A non-completable content item (authored requiredPlay 0 — iOS's
    // `items_not_required`) keeps its authored 0, so `isItemComplete` auto-
    // satisfies it (count 0 >= 0). Routing it through resolveRequiredPlay would
    // floor it to 1 and make any stage with an rp0 content item never roll up
    // (it never gets a play — the walker / iOS treat rp0 as not-required). Bug
    // surfaced by the Brief 149b cross-stage stage-completion assertion.
    if (!isCompletableItem(it)) {
      nodes.push({
        id, isGate, isMeter, isIntroItem, isGatingExempt,
        requiredPlay: typeof it?.requiredPlay === "number" ? it.requiredPlay : undefined,
      });
      return;
    }
    const prog = /** @type {{completedCount?: number, requiredPlay?: number} | undefined} */ (
      await getItemProgress(id)
    );
    completedCounts[String(id)] = prog?.completedCount ?? 0;
    nodes.push({ id, isGate, isMeter, isIntroItem, isGatingExempt, requiredPlay: resolveRequiredPlay(it, prog?.requiredPlay) });
  }));

  return isLessonComplete(nodes, { completedCounts });
}

/**
 * @param {ItemState[]} states
 * @returns {PathwayPage[]}
 */
function paginate(states) {
  /** @type {PathwayPage[]} */
  const pages = [];
  if (states.length === 0) return pages;

  for (let start = 0; start < states.length; start += PAGE_SIZE) {
    const nodes = states.slice(start, start + PAGE_SIZE);
    const isLast = start + PAGE_SIZE >= states.length;
    pages.push({
      nodes,
      startNumber: start + 1,
      hasNextPage: !isLast,
    });
  }
  return pages;
}

/**
 * Resolve whether a single lesson is locked at the pathway/stage level — the
 * exact lock model the squiggle renders. Finds the stage that contains
 * `lessonId`, runs that stage's lesson list through `pathwayFromOrderedNodes`
 * (which applies the canonical gating computation), and returns the matching
 * node's locked state. Shared by the lesson-detail » arrow and the
 * lesson-report Continue button so forward-navigation greys out when the
 * destination is gated (Brief 135 NAV-1/NAV-2).
 *
 * Conservative by design: returns `false` (i.e. NOT locked → navigable) when
 * gating is off, when the lesson can't be located, or on any load error. It
 * never *over*-locks; a `true` result means the lesson is genuinely gated.
 *
 * Node shape mirrors `kfe-pathway.js`'s `lessonEntryToNode` so this and the
 * rendered squiggle can never disagree about a lesson's lock state.
 *
 * Brief 164 (Bug 6): a lesson is a FOLDER — it never accrues its own
 * `completedCount` (nothing "plays" a lesson; `maybeFireLessonCompletedForItem`
 * sets only a separate `lessonCompletion` flag, never a folder playcount). So the
 * store-read path inside `pathwayFromOrderedNodes` saw every lesson as 0/1 →
 * incomplete, which systematically OVER-LOCKED the next lesson even after the
 * learner finished the current one — the »» chevron greyed while the squiggle
 * (correctly) showed the next lesson reachable. Fix: seed each lesson node's
 * `derivedComplete` from the persisted roll-up cache — the SAME source the stage
 * squiggle paints from (Brief 163 `getAllRollupCache`, keyed by lesson id with
 * `complete = deriveLessonComplete(...)`). Now the chevron's lock decision and
 * the squiggle's blue/green/grey can never disagree. Cache miss (e.g. a deep-link
 * before the stage screen was ever visited) → `derivedComplete: false`, which is
 * exactly what the squiggle paints on its own first paint before the background
 * refresh — conservative (never over-unlocks) and consistent.
 *
 * @param {string} pathwayId
 * @param {string|number} lessonId
 * @returns {Promise<boolean>}
 */
export async function isLessonLocked(pathwayId, lessonId) {
  try {
    if ((await getSetting("gating_enabled")) === false) return false;
    const tree = await loadPathway(pathwayId);
    const want = String(lessonId);
    /** @type {Map<string, boolean>} */
    let rollup = new Map();
    try {
      const rows = await getAllRollupCache();
      rollup = new Map(rows.map((/** @type {any} */ r) => [String(r.id), !!r.complete]));
    } catch (e) {
      console.warn("[pathway-builder] isLessonLocked roll-up read failed; assuming incomplete", e);
    }
    for (const stage of tree.stages || []) {
      const lessons = stage.lessons || [];
      if (!lessons.some((l) => String(l.id) === want)) continue;
      /** @type {OrderedPathwayNode[]} */
      const nodes = lessons.map((l) => {
        const isGate = l.type === "gate";
        const isIntroItem = l.isIntro === true;
        const isExempt = /** @type {any} */ (l).excludeFromGating === true;
        /** @type {OrderedPathwayNode} */
        const node = {
          itemId: l.id,
          title: l.titleEn ?? "",
          titleZh: l.titleZh,
          isGate,
          isIntroItem,
          // Brief 150: honour the exemption flag here too so this lock decision
          // can never disagree with the rendered squiggle.
          excludeFromGating: isExempt,
          gateRule: /** @type {any} */ (l.gateRule),
        };
        // Brief 164 (Bug 6): seed completion from the roll-up cache for the same
        // derivable lessons the squiggle derives (non-gate, non-intro,
        // non-exempt). Gates/intros keep `derivedComplete` undefined → the
        // existing store-read path, unchanged (and correct — they carry no
        // folder playcount either way).
        if (!isGate && !isIntroItem && !isExempt) {
          node.derivedComplete = rollup.get(String(l.id)) === true;
        }
        return node;
      });
      const pages = await pathwayFromOrderedNodes(nodes);
      for (const page of pages) {
        for (const node of page.nodes) {
          if (String(node.itemId) === want) return node.nodeState === "locked";
        }
      }
      // Found in the stage but filtered out of the rendered nodes (gate / intro
      // / meter) — those aren't lockable lesson destinations.
      return false;
    }
    return false;
  } catch (e) {
    console.warn("[pathway-builder] isLessonLocked failed", e);
    return false;
  }
}
